import {
  FormControl,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  SelectChangeEvent,
} from "@mui/material";

import {
  CHART_DISPLAY_TYPES,
  ChartDisplayType,
  NumericAggregation,
  NUMERIC_AGGREGATIONS,
} from "@/shared/domain/metrics";

import { CardConfig } from "../DashboardHeader/DashboardParams";

import styles from "./CardEditPopover.module.css";
import { FuzzySelect } from "./FuzzySelect";

export interface CardEditPopoverProps {
  /** The card being edited (controlled by the parent / URL). */
  card: CardConfig;
  /** All selectable metric names. */
  allNames: string[];
  /** The metric to show selected — the card's own, or its resolved fallback
   *  when unset/stale (see effectiveTrendMetric / effectiveChartMetric). */
  effectiveMetric: string;
  /** The card-menu button the popover anchors to. */
  anchorEl: HTMLElement;
  onClose: () => void;
  /** Fired with the updated card whenever the user changes a picker. */
  onChange: (next: CardConfig) => void;
}

/**
 * The "Edit Data" popover behind every card's ⋮ menu: metric + aggregation
 * pickers, plus the display-type picker for charts. Stays open across edits
 * (each change persists through `onChange` immediately) and closes on
 * click-away.
 */
export function CardEditPopover({
  card,
  allNames,
  effectiveMetric,
  anchorEl,
  onClose,
  onChange,
}: CardEditPopoverProps) {
  return (
    <Popover
      open
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      // Popover's paper receives focus on open but has no intrinsic name, so
      // without this a screen reader announces nothing about what opened.
      slotProps={{ paper: { "aria-label": "Edit card data" } }}
    >
      <div className={styles.editor}>
        <FuzzySelect
          label="Metric"
          value={effectiveMetric}
          options={allNames}
          onChange={(next) => onChange({ ...card, metric: next })}
        />
        <FuzzySelect
          label="Aggregation"
          value={card.aggregation}
          options={[...NUMERIC_AGGREGATIONS]}
          onChange={(next) =>
            onChange({ ...card, aggregation: next as NumericAggregation })
          }
        />
        {card.kind === "chart" && (
          <FormControl size="small">
            <InputLabel id={`card-edit-display-${card.id}`}>Display</InputLabel>
            <Select
              labelId={`card-edit-display-${card.id}`}
              label="Display"
              value={card.display}
              onChange={(e: SelectChangeEvent) =>
                onChange({
                  ...card,
                  display: e.target.value as ChartDisplayType,
                })
              }
            >
              {CHART_DISPLAY_TYPES.map((d) => (
                <MenuItem key={d.value} value={d.value}>
                  {d.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </div>
    </Popover>
  );
}
